import React, { useMemo } from 'react';
import {
  IconClipboardType,
  IconCloseButton,
  IconEye,
  IconFlag,
  IconMark,
  IconStopwatch,
  OrangeButton,
  SmallButton,
} from 'components';
import { COLORS } from 'utils/colors';
import dayjs from 'dayjs';
import { CLIENT_STATUS, DATE_FORMAT } from 'api/constants';

export const SingleClientTaskRow = ({ task, weekIndex, granted, clientStatus, activeWeek }) => {
  const generatedDate = task.assigned_at ? dayjs(task.assigned_at) : null;
  // const initialDate = dayjs(task.updated_at);

  const storeTaskButtons = useMemo(() => {
    if (task.completed) {
      return (
        <div className="flex items-center justify-end gap-x-6 w-full">
          <SmallButton
            icon={<IconEye className={'hover:text-darkGrey'} color={COLORS.blue} />}
            label="View"
            href={`/weekly-forms/${weekIndex}`}
          />
        </div>
      );
    } else if (task.task_id || (!task.task_id && weekIndex < activeWeek)) {
      return (
        <div className="flex items-center justify-end gap-x-6 w-full">
          <OrangeButton
            disabled={!task.task_id}
            href={`/weekly-forms/${weekIndex}`}
            label="Open"
            icon={<IconClipboardType className="min-w-6 min-h-6" />}
          />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-end gap-x-6 w-full">
        <OrangeButton
          disabled={!task.task_id}
          href={`/weekly-forms/${weekIndex}`}
          label="Open"
          icon={<IconClipboardType className="min-w-6 min-h-6" />}
        />
      </div>
    );
  }, [activeWeek, task.completed, task.task_id, weekIndex]);

  if (clientStatus === CLIENT_STATUS.not_started || weekIndex > activeWeek) {
    return (
      <div className="flex bg-lightGrey rounded-lg shadow-block-shadow h-[76px] overflow-hidden pointer-events-none">
        <div className={`flex items-center w-1/5  gap-x-4 px-5 bg-grey`}>
          <IconFlag color={COLORS.darkGrey} className="min-w-7" />
          <p className="text-24 font-normal text-darkGrey whitespace-nowrap">Week {weekIndex}</p>
        </div>
        <div className="w-4/5 flex items-center px-4 gap-x-8">
          <div className="w-1/4 flex items-center gap-x-1">
            <IconClipboardType />
            <p className="flex text-16 text-lightBlue">-</p>
          </div>
          <p className="text-14 font-medium w-1/4 text-lightBlue">-</p>
          <p className="text-14 font-medium w-1/4 text-lightBlue">-</p>
          <p className="text-14 text-lightBlue w-1/4">Upcoming</p>
          <div className="w-1/4 flex justify-end">
            <div className="flex items-center justify-end gap-x-6 w-full">
              <OrangeButton
                disabled
                href={`/weekly-forms/${weekIndex}`}
                label="Open"
                icon={<IconClipboardType className="min-w-6 min-h-6" />}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const bgColor = () => {
    if (task.completed) return 'bg-green';

    if (weekIndex >= activeWeek) return 'bg-grey';

    if (task.task_id) {
      if (activeWeek === weekIndex + 1) return 'bg-orange';
      if (activeWeek > weekIndex + 1) return 'bg-red';
    }

    return 'bg-grey';
  };
  const leftBg = bgColor();
  const leftIsGrey = leftBg === 'bg-grey';

  const iconType = () => {
    if (task.completed) return <IconMark color={COLORS.white} className="min-w-7" />;
    // If the left column is neutral grey (upcoming/current), use darkGrey icon color
    if (activeWeek === weekIndex + 1 && !task.completed)
      return (
        <IconStopwatch className="min-w-7" color={leftIsGrey ? COLORS.darkGrey : COLORS.white} />
      );
    if (activeWeek === weekIndex && clientStatus === CLIENT_STATUS.completed)
      return (
        <IconCloseButton
          color={leftIsGrey ? COLORS.darkGrey : COLORS.white}
          className="min-w-7 min-h-7"
        />
      );
    if (!task.completed) {
      return (
        <IconCloseButton
          color={leftIsGrey ? COLORS.darkGrey : COLORS.white}
          className="min-w-7 min-h-7"
        />
      );
    }
    return (
      <IconStopwatch className="min-w-7" color={leftIsGrey ? COLORS.darkGrey : COLORS.white} />
    );
  };

  return (
    <div className="flex bg-white rounded-lg shadow-block-shadow h-[76px] overflow-hidden">
      <div className={`flex items-center w-[20%] gap-x-4 px-5 ${bgColor()}`}>
        {iconType()}
        <p
          className={`text-24 font-normal whitespace-nowrap ${weekIndex === activeWeek ? 'text-darkGrey' : 'text-white'}`}
        >
          Week {weekIndex}
        </p>
      </div>
      <div className="w-4/5 flex items-center px-4 gap-x-8">
        <div className={`${task.task_id ? 'w-1/4' : 'w-2/4'} flex items-center gap-x-1`}>
          <IconClipboardType color={task.task_id ? COLORS.black : COLORS.lightBlue} />
          <p className="flex text-16 gap-x-1">
            {task.task_id ? (
              <span>
                <span className={`${task.completed ? 'text-green' : 'text-red'}`}>
                  {Number(task.completed)}
                </span>{' '}
                (1)
              </span>
            ) : (
              <span className="text-lightBlue text-sm">There are no form for this week yet</span>
            )}
          </p>
        </div>
        <p className={`text-14 font-medium ${task.task_id ? 'w-1/4' : 'w-0 text-[0]'}`}>
          {weekIndex <= activeWeek ? granted : '-'}
        </p>
        <p className="text-14 font-medium w-1/4">
          {generatedDate ? generatedDate.format(DATE_FORMAT) : '-'}
        </p>
        <p className="text-14 font-medium w-1/4">
          {(() => {
            if (weekIndex === activeWeek) return <span className="text-blue">Current</span>;
            if (task.completed) return <span className="text-green">Completed</span>;
            if (!generatedDate) return <span className="text-lightBlue">-</span>;
            const now = dayjs();
            const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
            const diff = now.valueOf() - generatedDate.valueOf();
            if (diff <= oneWeekMs) {
              const remaining = Math.ceil((oneWeekMs - diff) / (24 * 60 * 60 * 1000));
              return (
                <span className="text-orange flex items-center gap-x-2">
                  Pending <span className="text-lightBlue">({remaining} days left)</span>
                </span>
              );
            }
            return <span className="text-red">Overdue</span>;
          })()}
        </p>
        <div className="w-1/4 flex justify-end">{storeTaskButtons}</div>
      </div>
    </div>
  );
};
